import { useCallback, useEffect, useState } from 'react';

export interface NfcReadResult {
  serialNumber: string;
  records?: Array<{
    recordType: string;
    data: string;
  }>;
}

export interface UseNfcScannerResult {
  isSupported: boolean;
  isScanning: boolean;
  error: string | null;
  lastRead: NfcReadResult | null;
  startScan: () => Promise<void>;
  stopScan: () => void;
  clearError: () => void;
  clearLastRead: () => void;
}

declare global {
  interface Window {
    NDEFReader?: new () => NDEFReader;
  }

  interface NDEFReader {
    scan: (options?: { signal?: AbortSignal }) => Promise<void>;
    addEventListener: (type: 'reading' | 'readingerror', listener: (event: NDEFReadingEvent | Event) => void) => void;
    removeEventListener: (type: 'reading' | 'readingerror', listener: (event: NDEFReadingEvent | Event) => void) => void;
  }

  interface NDEFReadingEvent extends Event {
    serialNumber: string;
    message: {
      records: Array<{
        recordType: string;
        data: ArrayBuffer;
      }>;
    };
  }
}

export const useNfcScanner = (): UseNfcScannerResult => {
  const [isSupported, setIsSupported] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRead, setLastRead] = useState<NfcReadResult | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  useEffect(() => {
    const supported = 'NDEFReader' in window;
    setIsSupported(supported);
  }, []);

  const startScan = useCallback(async () => {
    if (!isSupported) {
      setError('NFC wird auf diesem Gerät nicht unterstützt');
      return;
    }

    if (isScanning) {
      return;
    }

    setError(null);
    setLastRead(null);
    setIsScanning(true);

    try {
      const controller = new AbortController();
      setAbortController(controller);

      const NDEFReaderClass = window.NDEFReader;
      if (!NDEFReaderClass) {
        throw new Error('NDEFReader not available');
      }

      const reader = new NDEFReaderClass();
      const resolveTagId = (rawSerialNumber: string | undefined, records: Array<{ data: string }>): string | null => {
        const fromSerial = rawSerialNumber?.trim();
        if (fromSerial) {
          return fromSerial;
        }
        const fromRecord = records
          .map((record) => record.data?.trim())
          .find((value) => value && value.length > 0);
        return fromRecord && fromRecord.length > 0 ? fromRecord : null;
      };

      reader.addEventListener('reading', (event: Event) => {
        const ndefEvent = event as NDEFReadingEvent;
        const records = (ndefEvent.message?.records ?? []).map((record) => {
          const decoder = new TextDecoder();
          return {
            recordType: record.recordType,
            data: decoder.decode(record.data)
          };
        });

        const tagId = resolveTagId(ndefEvent.serialNumber, records);

        if (!tagId) {
          alert('NFC-Tag konnte nicht gelesen werden (keine Seriennummer)');
          setError('NFC-Tag konnte nicht gelesen werden');
          setIsScanning(false);
          controller.abort();
          setAbortController(null);
          return;
        }

        setLastRead({
          serialNumber: tagId,
          records
        });
        setIsScanning(false);
        controller.abort();
        setAbortController(null);
      });

      reader.addEventListener('readingerror', () => {
        alert('Fehler beim Lesen des NFC-Tags');
        setError('Fehler beim Lesen des NFC-Tags');
        setIsScanning(false);
        controller.abort();
        setAbortController(null);
      });

      await reader.scan({ signal: controller.signal });
    } catch (scanError) {
      console.error('NFC scan error:', scanError);
      if (scanError instanceof Error) {
        if (scanError.name === 'NotAllowedError') {
          setError('NFC-Zugriff wurde nicht erlaubt. Bitte Berechtigung erteilen.');
        } else if (scanError.name === 'NotSupportedError') {
          setError('NFC wird auf diesem Gerät nicht unterstützt');
        } else if (scanError.name === 'AbortError') {
          // Scan was cancelled, not an error
        } else {
          alert('Fehler beim Starten des NFC-Scanners');
          setError('Fehler beim Starten des NFC-Scanners');
        }
      } else {
        alert('Fehler beim Starten des NFC-Scanners');
        setError('Fehler beim Starten des NFC-Scanners');
      }
      setIsScanning(false);
      setAbortController(null);
    }
  }, [isSupported, isScanning]);

  const stopScan = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsScanning(false);
  }, [abortController]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearLastRead = useCallback(() => {
    setLastRead(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  return {
    isSupported,
    isScanning,
    error,
    lastRead,
    startScan,
    stopScan,
    clearError,
    clearLastRead
  };
};
